import React, {
  Suspense,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import Logger from '../../../../shared/Logger';
import {
  Alert,
  Flex,
  Box,
  Button,
  Spinner,
  Text,
  Panel,
  theme,
} from '@xometry/xometry_loft';
import { API_ORIGIN, WWW_ORIGIN } from '../../../../config';
import { useQuery, useMutation } from '@apollo/client';
import {
  CreateOrganizationQuotePresetMutationResult,
  CreateOrganizationQuotePresetMutationVariables,
  CreateOrganizationQuotePresetQuery,
  GetOrganizationDefaultsByOrganizationIdsQuery,
  QuotePatchRequirement,
  UpdateOrganizationQuotePresetMutationResult,
  UpdateOrganizationQuotePresetMutationVariables,
  UpdateOrganizationQuotePresetQuery,
  DeleteOrganizationQuotePresetMutationResult,
  DeleteOrganizationQuotePresetMutationVariables,
  DeleteOrganizationQuotePresetQuery,
  OrganizationQuotePreset,
} from '../../../../schema/generated/schema';
import { SuccessToastQuotePresets } from '../../../../components/SuccessToast';
import PresetsList from './PresetsList';
import { css } from '@emotion/react';
import WarningToast from '../../../../components/WarningToast';
import { lazyWithRetries } from '../../../../utils/shimport';
import {
  isQuotePresetPatchEmpty,
  type QuotePresetSavePatch,
} from './quotePresetPatch';

type Body = {
  orgId: string;
  menuId: string;
  quotePresets: {
    menuId: string;
    id: number;
    organizationId: number;
    patchRequirement: QuotePresetSavePatch;
  };
};

type QuotePresetsProps = {
  userId: string;
  isStaff: boolean;
  orgId: string;
  hasWriteOrgPermission: boolean;
  onOnboardingActionCompleted: () => void;
};

const QuotePresets = (props: QuotePresetsProps) => {
  const {
    userId,
    isStaff,
    orgId,
    hasWriteOrgPermission,
    onOnboardingActionCompleted,
  } = props;
  const [addOrEditView, setAddOrEditView] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [selectedPatchRequirement, setSelectedPatchRequirement] =
    useState<QuotePatchRequirement | null>(null);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [hasInvalidRequirements, setHasInvalidRequirements] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [duplicateMenuPreset, setDuplicateMenuPreset] =
    useState<OrganizationQuotePreset | null>(null);
  const [menusAlreadyHavePresets, setMenusAlreadyHavePresets] = useState<
    string[]
  >([]);

  const {
    loading: defaultsLoading,
    data: defaultsData,
    refetch: refetchDefaults,
  } = useQuery(GetOrganizationDefaultsByOrganizationIdsQuery, {
    skip: !orgId,
    variables: {
      organizationIds: [orgId],
    },
  });
  const orgDefaults = defaultsData?.getOrganizationDefaultsByOrganizationIds[0];

  const [createQuotePreset] = useMutation<
    CreateOrganizationQuotePresetMutationResult,
    CreateOrganizationQuotePresetMutationVariables
  >(CreateOrganizationQuotePresetQuery, {
    refetchQueries: [
      GetOrganizationDefaultsByOrganizationIdsQuery,
      'GetOrganizationDefaultsByOrganizationIds',
    ],
  });

  useEffect(() => {
    if (orgDefaults?.quotePresets) {
      const invalidRequirements = orgDefaults.quotePresets.filter(
        (preset: OrganizationQuotePreset) => preset.invalidRequirement
      );
      setHasInvalidRequirements(invalidRequirements.length > 0);
      setMenusAlreadyHavePresets(
        orgDefaults.quotePresets.map(
          (preset: OrganizationQuotePreset) => preset.menuId
        )
      );
    }
  }, [orgDefaults]);

  const [updateQuotePreset] = useMutation<
    UpdateOrganizationQuotePresetMutationResult,
    UpdateOrganizationQuotePresetMutationVariables
  >(UpdateOrganizationQuotePresetQuery, {
    refetchQueries: [
      GetOrganizationDefaultsByOrganizationIdsQuery,
      'GetOrganizationDefaultsByOrganizationIds',
    ],
  });

  const [deleteQuotePreset] = useMutation<
    DeleteOrganizationQuotePresetMutationResult,
    DeleteOrganizationQuotePresetMutationVariables
  >(DeleteOrganizationQuotePresetQuery, {
    refetchQueries: [
      GetOrganizationDefaultsByOrganizationIdsQuery,
      'GetOrganizationDefaultsByOrganizationIds',
    ],
  });

  const handleSavePreset = async (body: Body) => {
    const patch = body.quotePresets.patchRequirement;
    if (isQuotePresetPatchEmpty(patch)) {
      if (!selectedPresetId) {
        handleCloseEditView();
        return;
      }
      // Edit flow: persist empty requirements via update (same as list "Standard Inspection" row).
      // Do not auto-delete — that was easy to trigger when the form sent `requirements` but not `add_requirements`.
    }

    let isSuccess = false;
    if (selectedPresetId) {
      try {
        const { data, errors } = await updateQuotePreset({
          variables: body as UpdateOrganizationQuotePresetMutationVariables,
        });

        if (errors?.length || !data?.updateOrganizationQuotePreset) {
          Logger.error(
            'Update quote preset returned no data or GraphQL errors',
            {
              component: 'QuotePresets',
              action: 'updatePreset',
              errors,
            }
          );
          setHasError(true);
        } else {
          isSuccess = true;
          setHasError(false);
          refetchDefaults();
        }
      } catch (error) {
        Logger.error('Error updating quote preset', error, {
          component: 'QuotePresets',
          action: 'updatePreset',
        });
        setHasError(true);
      }
    } else {
      try {
        const { data, errors } = await createQuotePreset({
          variables: body as CreateOrganizationQuotePresetMutationVariables,
        });

        if (errors?.length || !data?.createOrganizationQuotePreset) {
          Logger.error(
            'Create quote preset returned no data or GraphQL errors',
            {
              component: 'QuotePresets',
              action: 'createPreset',
              errors,
            }
          );
          setHasError(true);
        } else {
          isSuccess = true;
          setHasError(false);
          refetchDefaults();
          onOnboardingActionCompleted();
        }
      } catch (error) {
        Logger.error('Error creating quote preset', error, {
          component: 'QuotePresets',
          action: 'createPreset',
        });
        setHasError(true);
      }
    }
    if (isSuccess) {
      if (window.analytics) {
        window.analytics.track(
          `Quote Preset ${selectedPresetId ? 'Updated' : 'Created'}`,
          {
            staffCreated: isStaff ? 'Yes' : 'No',
            user: userId,
            orgId: orgId,
            preset: body,
          }
        );
      }
      setAddOrEditView(false);
      setIsSuccessful(true);
      setSelectedPatchRequirement(null);
      setSelectedPresetId(null);
    }
  };

  const toggleEditView = (
    presetId: string,
    patchRequirement: QuotePatchRequirement
  ) => {
    setAddOrEditView(true);
    setSelectedPresetId(presetId);
    const newRequirement = {
      ...patchRequirement,
      requirements: patchRequirement.add_requirements,
    };

    delete newRequirement.add_requirements;
    setSelectedPatchRequirement(newRequirement);
  };

  const handleCloseEditView = () => {
    setSelectedPresetId(null);
    setSelectedPatchRequirement(null);
    setAddOrEditView(false);
  };

  const handleDeletePreset = async (presetId: string) => {
    const body = {
      orgId,
      quotePresetId: parseInt(presetId),
    };
    try {
      const { data: deleteResult, errors: deleteErrors } =
        await deleteQuotePreset({
          variables: body as DeleteOrganizationQuotePresetMutationVariables,
        });

      if (
        deleteErrors?.length ||
        !deleteResult?.deleteOrganizationQuotePreset
      ) {
        Logger.error('Delete quote preset returned no data or GraphQL errors', {
          component: 'QuotePresets',
          action: 'deletePreset',
          errors: deleteErrors,
          deleteResult,
        });
        setHasError(true);
      } else {
        if (window.analytics) {
          window.analytics.track(`Quote Preset Deleted`, {
            staffCreated: isStaff ? 'Yes' : 'No',
            user: userId,
            presetId: presetId,
            orgId: orgId,
          });
        }
        setIsSuccessful(true);
        handleCloseEditView();
        setHasError(false);
        refetchDefaults();
      }
    } catch (error) {
      Logger.error('Error deleting quote preset', error, {
        component: 'QuotePresets',
        action: 'deletePreset',
      });
      setHasError(true);
    }
  };

  const hasExistingPreset = useCallback(
    (selectedMenu: string) => {
      if (orgDefaults?.quotePresets) {
        const duplicatePreset = orgDefaults.quotePresets.find(
          (preset: OrganizationQuotePreset) => preset.menuId === selectedMenu
        );
        setDuplicateMenuPreset(duplicatePreset);

        if (duplicatePreset) {
          return true;
        }
      }
      return false;
    },
    [orgDefaults]
  );

  const QuotePresetsFormURL = `${WWW_ORIGIN}/components/requirements-editing-component/quote_presets.mjs`;
  const LazyQuotePresetsForm = lazyWithRetries(QuotePresetsFormURL);

  const memoizedQuotePresetsForm = useMemo(
    () => (
      <Suspense fallback={<Spinner />}>
        <LazyQuotePresetsForm
          wwwRoot={WWW_ORIGIN}
          apiRoot={API_ORIGIN}
          onSavePreset={handleSavePreset}
          onDeletePreset={handleDeletePreset}
          onToggleView={() => handleCloseEditView()}
          isStaff={isStaff}
          editMode={addOrEditView && selectedPresetId}
          orgId={orgId}
          currentPreset={selectedPatchRequirement}
          presetId={selectedPresetId}
          hasError={hasError}
          onMenuSelection={hasExistingPreset}
          menusAlreadyHavePresets={menusAlreadyHavePresets}
        />
      </Suspense>
    ),
    [
      selectedPresetId,
      selectedPatchRequirement,
      addOrEditView,
      hasError,
      orgId,
      userId,
      isStaff,
    ]
  );

  return (
    <Flex flexDirection="column" gap={theme.space[4]}>
      {isSuccessful && (
        <SuccessToastQuotePresets
          isSuccessful={isSuccessful}
          setIsSuccessful={setIsSuccessful}
          message="Preset has been saved"
        />
      )}
      {!hasWriteOrgPermission && <WarningToast />}

      <Text kind="subpage-title">Quote Presets</Text>
      <Panel kind={theme.kind.primary} backgroundColor={theme.colors.blues[5]}>
        <Flex flexDirection="column" gap={theme.space[3]}>
          <Flex
            alignItems="center"
            gap={theme.space[2]}
            fontWeight="600"
            color={theme.colors.darkBlues[90]}
          >
            <i className="far fa-sliders" />
            <Text kind="panel-title" fontWeight={theme.fontWeights.semibold}>
              What are Quote Presets?
            </Text>
          </Flex>
          <Text>
            Quote Presets automatically apply required inspections and
            certifications to new quotes. Only Team admins can manage Quote
            Presets, members can still edit quotes that have Quote Presets
            applied.
          </Text>
        </Flex>
      </Panel>
      {hasInvalidRequirements && (
        <Alert kind={theme.kind.warning}>
          <i
            className="far fa-info-circle"
            css={css`
              margin-left: ${theme.space[1]};
              margin-right: ${theme.space[2]};
              font-size: ${theme.fontSizes[2]};
            `}
          />
          The highlighted presets below include an inspection or certificate
          that is no longer supported. Edit your Quote Presets to select a new
          inspection or certificate.
        </Alert>
      )}
      <Panel kind={theme.kind.primary} flexDirection="column">
        <Box marginBottom={theme.space[3]}>
          {addOrEditView ? (
            <Text kind="panel-title" fontWeight={theme.fontWeights.semibold}>
              {selectedPresetId ? 'Edit Quote Preset' : 'Add Quote Presets'}
            </Text>
          ) : (
            <Text kind="panel-title" fontWeight={theme.fontWeights.semibold}>
              Quote Presets
            </Text>
          )}{' '}
        </Box>
        {!addOrEditView && hasWriteOrgPermission && (
          <Button
            marginTop={theme.space[5]}
            marginBottom={theme.space[3]}
            kind="text-blue"
            onClick={() => setAddOrEditView(true)}
          >
            <i className="far fa-plus-circle" /> Add Preset
          </Button>
        )}
        {addOrEditView ? (
          <Flex alignItems="flex-start" gap={theme.space[5]}>
            <Box position="relative">
              {duplicateMenuPreset && (
                <Box
                  position="absolute"
                  bottom={selectedPresetId ? '46px' : '0'}
                  height={`calc(100% - 175px)`}
                  width="100%"
                  style={{
                    cursor: 'not-allowed',
                  }}
                />
              )}
              {memoizedQuotePresetsForm}
            </Box>

            {duplicateMenuPreset &&
              !selectedPresetId &&
              hasWriteOrgPermission && (
                <Flex position="relative" top="155px">
                  <Button
                    kind="text-blue"
                    onClick={() => {
                      setDuplicateMenuPreset(null);
                      toggleEditView(
                        duplicateMenuPreset.id,
                        duplicateMenuPreset.patchRequirement
                      );
                    }}
                  >
                    Edit
                  </Button>
                </Flex>
              )}
          </Flex>
        ) : (
          !defaultsLoading && (
            <PresetsList
              isStaff={isStaff}
              teamInfo={orgDefaults}
              toggleEditView={toggleEditView}
              hasWriteOrgPermission={hasWriteOrgPermission}
            />
          )
        )}
      </Panel>
    </Flex>
  );
};

export default QuotePresets;
